import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TotpSetupStep from './TotpSetupStep';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';
import SessionTimer from '../components/SessionTimer';
import AdminAuditLog from './AdminAuditLog';
import { useAuth } from '../context/AuthContext';
import { getHierarchy } from '../services/authentikAPI';
import {
  API_BASE, page, cardScroll, inp, lbl, btn, btnDisabled, btnSecondary,
  RESEND_COOLDOWN_SECONDS, ConfirmDialog, DeleteUserDialog, statusBadge,
} from './AdminUsers';

// Same list CompleteProfile.jsx offers during self-service profile
// completion — kept in sync here so an admin editing Designation picks
// from the identical set, rather than being able to type something
// CompleteProfile.jsx's own approval-check (OFFICE_DESIGNATION matching)
// wouldn't recognize.
const DESIGNATIONS = [
  'Chief Engineer(Ele)', 'Superintending Engineer(Ele)', 'Executive Engineer',
  'Assistant Executive Engineer', 'Assistant Engineer', 'Junior Engineer',
  'Lineman', 'Junior Lineman', 'Power Man', 'Meter Reader', 'AO', 'AAO',
  'Junior Assistant', 'Senior Assistant', 'Office Account'
];

const ACCOUNT_TYPES = ['Office_account', 'Officer', 'Employee'];

// Compact variants of AdminUsers.jsx's inp/btn/btnSecondary — those are
// sized for standalone full-page forms (Login.jsx etc.); the admin phone-
// verification code step is a small inline control embedded in a profile
// page that already uses much smaller buttons elsewhere (Update/Remove
// above it), so the full-size styles looked oversized next to them.
const smallInp = { padding: '6px 10px', border: '1.5px solid #D3D1C7', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' };
const smallBtn = { padding: '6px 14px', background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' };
const smallBtnDisabled = { ...smallBtn, opacity: 0.6, cursor: 'not-allowed' };
const smallBtnSecondary = { ...smallBtn, background: 'none', border: '1px solid #D3D1C7', color: '#666' };

// Split out of AdminUsers.jsx (2026-08-05) — was an inline panel below
// whatever list was showing there; now its own page reached by clicking a
// row (/admin/users/:pk), since a growing user directory meant scrolling
// past an ever-longer list to reach it. Fetches its own data by pk (not
// received via navigation state) so a direct URL/bookmark/refresh works.
//
// UPDATED 2026-08-05: also surfaces the full set of custom attributes
// (zone, circle, division, subdivision, section, account type) — these
// existed in the API response all along (CompleteProfile.jsx writes them)
// but the old inline panel never showed any of them, only email/phone.

// Hierarchy/profile fields collected by CompleteProfile.jsx and bulk
// onboarding — read-only here, shown for admin reference only. Not
// editable from this panel; the user's own Complete Profile flow (or a
// future dedicated admin edit, if ever needed) is the actual source.
const ATTRIBUTE_FIELDS = [
  { key: 'designation', label: 'Designation' },
  { key: 'role', label: 'Role' },
  { key: 'accountType', label: 'Account Type' },
  // The single most-specific posting location on file (see toDirectoryUser
  // in adminUsers.js) — shown ahead of the raw Zone/Circle/.../Section
  // breakdown below since it's the one meaningful answer to "where does
  // this person work," including for a standalone entry (AdminHierarchy.jsx's
  // independent-entry toggle — e.g. "BESCOM Corporate Office") where it's
  // the only one of these six that's actually set.
  { key: 'office', label: 'Working Place' },
  { key: 'zone', label: 'Zone/Office' },
  { key: 'circle', label: 'Circle' },
  { key: 'division', label: 'Division' },
  { key: 'subdivision', label: 'Subdivision' },
  { key: 'section', label: 'Section' },
];

// Same exclusion AdminGroups.jsx's own list applies server-side (adminGroups.js)
// — kept in sync here too so this page's "current groups"/"add to group"
// picker never shows Authentik's own built-in groups or the hierarchy-store/
// apps-store service accounts' groups as something to add a real employee
// to. CONFIRMED (2026-09-10): this had drifted out of sync with the
// server-side list (missing bescom-apps-store) — fixed alongside adding
// authentik Agent-Users to both.
const EXCLUDED_GROUPS = new Set(['authentik Read-only', 'authentik Admins', 'authentik Agent-Users', 'bescom-hierarchy-store', 'bescom-apps-store']);

export default function AdminUserDetail() {
  const navigate = useNavigate();
  const { pk } = useParams();
  const { user: viewer } = useAuth();
  const isSuperAdmin = (viewer?.profile?.groups || []).includes('bescom-super-admins');

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState(null);
  const [emailProposed, setEmailProposed] = useState(false);
  const [cancellingEmail, setCancellingEmail] = useState(false);
  // Collapsed by default — the "New email address"/"New phone number"
  // forms used to always render open below the current value, even when
  // nobody wanted to change anything. An "Update" button next to the
  // current value now reveals the form instead.
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancellingPhone, setCancellingPhone] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  const [removingEmail, setRemovingEmail] = useState(false);
  const [removingPhone, setRemovingPhone] = useState(false);

  const [resendingPhone, setResendingPhone] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [togglingActive, setTogglingActive] = useState(false);

  // Working Details & Role — editable by an admin (previously read-only,
  // only settable via CompleteProfile.jsx or a bulk CSV upload). Collapsed
  // to a read-only grid by default, same "Update"/"Close" toggle pattern
  // as the email/phone sections above.
  const [hierarchy, setHierarchy] = useState([]);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);

  useEffect(() => {
    getHierarchy().then(setHierarchy).catch(() => {
      // Best-effort — the cascading Zone/Circle/.../Section selects just
      // won't have options if this fails; Designation/Role/Account Type
      // editing still works independently.
    });
  }, []);

  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const [resettingPassword, setResettingPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  const [resettingTotp, setResettingTotp] = useState(false);
  const [totpChallenge, setTotpChallenge] = useState(null);
  const [confirmingTotp, setConfirmingTotp] = useState(false);
  const [totpDone, setTotpDone] = useState(false);
  const [resettingPasskey, setResettingPasskey] = useState(false);
  const [passkeyReset, setPasskeyReset] = useState(null); // { ceremonyUrl, removed, expiresInMinutes }
  const [passkeyLinkCopied, setPasskeyLinkCopied] = useState(false);
  // SMS and one-time code sign-in are both plain attributes (see backend
  // lib/attributeMfa.js), so one reset flow serves both — `kind` is 'sms'
  // or 'code'.
  const [resettingCodeMfa, setResettingCodeMfa] = useState(null);
  const [codeMfaResetDone, setCodeMfaResetDone] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState(null);

  const [allGroups, setAllGroups] = useState([]);
  const [groupToAdd, setGroupToAdd] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [removingGroupPk, setRemovingGroupPk] = useState(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Re-reads the account WITHOUT touching loading/error/other panel state — used
  // right after a recovery action so the buttons flip between "Reset X" and
  // "Set up X" to match, while the passkey link / "done" messages stay put.
  async function refreshSelectedQuietly() {
    try {
      const res = await fetch(`${API_BASE}/admin/users/${pk}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSelected((prev) => (prev ? { ...prev, ...data } : data));
    } catch (e) {
      // best-effort — the buttons just stay as they were until the next load
    }
  }

  async function fetchUser() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${pk}`, {
        credentials: 'include',
      });
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        return;
      }
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 404) {
        setError('This user no longer exists.');
        setSelected(null);
        return;
      }
      const data = await res.json();
      setSelected(data);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
  }

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check;
  // fetchUser()'s own 401/403 handling already covers both cases.
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pk]);

  // CONFIRMED BUG (2026-09-08): fetchUser() above is a one-time load (plus
  // after a handful of specific admin actions) — the Session Timer below
  // just keeps ticking against whatever loginAt it was originally handed,
  // with nothing to ever notice the viewed person logged out somewhere
  // else in the meantime (an admin isn't the one whose session ended, so
  // there's no watchdog/redirect reacting to it the way there is for
  // someone viewing their own Dashboard). A silent background refresh —
  // deliberately not routed through fetchUser() itself, which would
  // flash the loading spinner and clear any visible error every 30s —
  // just updates loginAt so SessionTimer correctly disappears the moment
  // the backend actually clears it (see loginTime.js's clearLoginTime,
  // called from every real logout path). Stops polling once loginAt is
  // already null; nothing left to watch for at that point.
  useEffect(() => {
    if (!selected?.loginAt) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/users/${pk}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setSelected((prev) => (prev ? { ...prev, loginAt: data.loginAt } : prev));
      } catch (e) {
        // best-effort — a network blip just means the timer keeps
        // showing its last-known value until the next successful tick.
      }
    }, 30000);
    return () => clearInterval(id);
  }, [pk, selected?.loginAt]);

  // For the "add to group" picker below — fetched once per page visit,
  // same list AdminGroups.jsx shows (already excludes Authentik's own
  // built-in groups and bescom-hierarchy-store server-side).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/groups`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        setAllGroups(data.results || []);
      } catch (e) {
        // Non-fatal — the groups section just won't offer an "add" picker.
      }
    })();
  }, []);

  // Same cascading Zone → Circle → Division → Sub-Division → Section
  // logic as CompleteProfile.jsx (including "office" detection — a level
  // with nothing beneath it can be picked without drilling further) —
  // operating on detailsForm instead of that page's own form state.
  const getZones = () => [...new Set(hierarchy.map(h => h.zone))].filter(Boolean);
  const getCircles = () => !detailsForm?.zone ? [] : [...new Set(hierarchy.filter(h => h.zone === detailsForm.zone).map(h => h.circle))].filter(Boolean);
  const getDivisions = () => !detailsForm?.circle ? [] : [...new Set(hierarchy.filter(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle).map(h => h.division))].filter(Boolean);
  const getSubDivisions = () => !detailsForm?.division ? [] : [...new Set(hierarchy.filter(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle && h.division === detailsForm.division).map(h => h.subdivision))].filter(Boolean);
  const getSections = () => !detailsForm?.subdivision ? [] : [...new Set(hierarchy.filter(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle && h.division === detailsForm.division && h.subdivision === detailsForm.subdivision).map(h => h.section))].filter(Boolean);

  const isZoneOffice = detailsForm?.zone && hierarchy.some(h => h.zone === detailsForm.zone && !h.circle);
  const isCircleOffice = detailsForm?.circle && hierarchy.some(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle && !h.division);
  const isDivisionOffice = detailsForm?.division && hierarchy.some(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle && h.division === detailsForm.division && !h.subdivision && !h.section);
  const isSubdivisionOffice = detailsForm?.subdivision && hierarchy.some(h => h.zone === detailsForm.zone && h.circle === detailsForm.circle && h.division === detailsForm.division && h.subdivision === detailsForm.subdivision && !h.section);

  function handleEditDetails() {
    if (!selected) return;
    setDetailsForm({
      designation: selected.designation || '',
      role: selected.role || '',
      accountType: selected.accountType || '',
      zone: selected.zone || '',
      circle: selected.circle || '',
      division: selected.division || '',
      subdivision: selected.subdivision || '',
      section: selected.section || '',
    });
    setEditingDetails(true);
  }

  function handleCancelDetails() {
    setEditingDetails(false);
    setDetailsForm(null);
  }

  function handleDetailsChange(e) {
    const { name, value } = e.target;
    setDetailsForm((prev) => {
      const updated = { ...prev, [name]: value };
      // Same cascade-reset as CompleteProfile.jsx — picking a shallower
      // level invalidates whatever was chosen beneath it.
      if (name === 'zone') { updated.circle = ''; updated.division = ''; updated.subdivision = ''; updated.section = ''; }
      else if (name === 'circle') { updated.division = ''; updated.subdivision = ''; updated.section = ''; }
      else if (name === 'division') { updated.subdivision = ''; updated.section = ''; }
      else if (name === 'subdivision') { updated.section = ''; }
      return updated;
    });
  }

  async function handleSaveDetails(e) {
    e.preventDefault();
    if (!selected || !detailsForm) return;
    setSavingDetails(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(detailsForm),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not save working details.');
        return;
      }
      setEditingDetails(false);
      setDetailsForm(null);
      // Refetch rather than patch `selected` locally — `office` (shown
      // above these fields) is computed server-side from whichever of
      // these is deepest, and isn't part of what this endpoint returns.
      await fetchUser();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setSavingDetails(false);
    }
  }

  async function handleAddGroup(e) {
    e.preventDefault();
    if (!selected || !groupToAdd) return;
    setAddingGroup(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${groupToAdd}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userPk: selected.pk }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not add user to group.');
        return;
      }
      setGroupToAdd('');
      await fetchUser();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setAddingGroup(false);
    }
  }

  function handleRemoveGroup(group) {
    if (!selected) return;
    setConfirmDialog({
      title: 'Remove from Group',
      message: `Remove ${selected.username} from ${group.name}?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: () => doRemoveGroup(group),
    });
  }

  async function doRemoveGroup(group) {
    if (!selected) return;
    setConfirmDialog(null);
    setRemovingGroupPk(group.pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${group.pk}/members/${selected.pk}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not remove user from group.');
        return;
      }
      await fetchUser();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setRemovingGroupPk(null);
    }
  }

  async function handleSendEmail(e) {
    e.preventDefault();
    if (!selected || !emailInput.trim()) return;
    setEmailSending(true);
    setError(null);
    setEmailMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/propose-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setEmailMessage(data.message);
      setEmailProposed(true);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setEmailSending(false);
    }
  }

  async function handleCancelEmail() {
    if (!selected) return;
    setCancellingEmail(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/cancel-email`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setEmailMessage(null);
      setEmailProposed(false);
      setEmailInput('');
      setShowEmailForm(false);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setCancellingEmail(false);
    }
  }

  async function handleSendPhoneCode(e) {
    e.preventDefault();
    if (!selected || phoneInput.length !== 10) return;
    setPhoneSending(true);
    setError(null);
    setPhoneMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/propose-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phoneInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setPhoneMessage(data.message);
      setAwaitingCode(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setPhoneSending(false);
    }
  }

  // Re-sends a fresh code to the same phone number without the admin having
  // to Cancel and retype it — same propose-phone endpoint as the initial
  // send, since (unlike the target user's own OTP form) this is only ever
  // triggered by an explicit click, never on mount/refresh, so there's no
  // need for the reuse-existing-code dedup phoneVerification.js has.
  async function handleResendPhoneCode() {
    if (!selected || phoneInput.length !== 10) return;
    setResendingPhone(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/propose-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phoneInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setPhoneMessage(data.message);
      setCodeInput('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResendingPhone(false);
    }
  }

  async function handleConfirmPhone(e) {
    e.preventDefault();
    if (!selected || codeInput.length !== 6) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/confirm-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: codeInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code.');
        return;
      }
      setPhoneMessage(`Phone number confirmed: ${data.phone}`);
      setAwaitingCode(false);
      setCodeInput('');
      setShowPhoneForm(false);
      setSelected((prev) => ({ ...prev, phone: data.phone }));
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setConfirming(false);
    }
  }

  // Removal is admin-only and immediate, unlike setting a new value — there's
  // no new contact info here that needs the target user's own verification,
  // just clearing an existing one. Reuses the PATCH /:pk endpoint that
  // propose/confirm above deliberately bypass (see adminUsers.js), which
  // already enforces "can't leave both empty" server-side.
  function handleRemoveEmail() {
    if (!selected || !selected.email) return;
    setConfirmDialog({
      title: 'Remove Email',
      message: `Remove the email address from ${selected.username}'s account?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: doRemoveEmail,
    });
  }

  async function doRemoveEmail() {
    setConfirmDialog(null);
    setRemovingEmail(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: '' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setSelected((prev) => ({ ...prev, email: data.email }));
      setEmailMessage(null);
      setEmailProposed(false);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setRemovingEmail(false);
    }
  }

  function handleRemovePhone() {
    if (!selected || !selected.phone) return;
    setConfirmDialog({
      title: 'Remove Phone',
      message: `Remove the phone number from ${selected.username}'s account?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: doRemovePhone,
    });
  }

  async function doRemovePhone() {
    setConfirmDialog(null);
    setRemovingPhone(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: '' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setSelected((prev) => ({ ...prev, phone: data.phone }));
      setPhoneMessage(null);
      setAwaitingCode(false);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setRemovingPhone(false);
    }
  }

  // Also reuses PATCH /:pk (see adminUsers.js) — is_active is unrelated to
  // the email/phone "must keep at least one" logic, so it's a clean
  // independent field on the same endpoint. Backend also blocks an admin
  // deactivating their own account, since there'd be no way back in
  // through this panel afterward.
  function handleToggleActive() {
    if (!selected) return;
    const nextActive = !selected.is_active;
    const verb = nextActive ? 'Activate' : 'Deactivate';
    setConfirmDialog({
      title: `${verb} Account`,
      message: `${verb} ${selected.username}'s account?${nextActive ? '' : ' They will not be able to sign in until reactivated.'}`,
      confirmLabel: verb,
      danger: !nextActive,
      onConfirm: doToggleActive,
    });
  }

  async function doToggleActive() {
    if (!selected) return;
    const nextActive = !selected.is_active;
    setConfirmDialog(null);
    setTogglingActive(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: nextActive }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      // Reachable only from an already-active account (the button toggles
      // active<->inactive), so landing on false here is always a genuine
      // admin deactivation, never "still registering" — see registrationStatus
      // on the /directory response for how that distinction is made there.
      const registrationStatus = data.is_active ? 'active' : 'deactivated';
      setSelected((prev) => ({ ...prev, is_active: data.is_active, registrationStatus }));
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setTogglingActive(false);
    }
  }

  // Permanent, unlike Activate/Deactivate above — gated by DeleteUserDialog
  // requiring the username to be typed out, not just a click, since this
  // can't be undone the way deactivation can.
  function handleDeleteUser() {
    if (!selected) return;
    setDeleteDialog({ pk: selected.pk, username: selected.username, onConfirm: doDeleteUser });
  }

  async function doDeleteUser() {
    if (!selected) return;
    setDeleteDialog(null);
    setDeletingUser(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not delete user.');
        return;
      }
      navigate('/admin/users');
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setDeletingUser(false);
    }
  }

  // Applied immediately, unlike email/phone above — password reset needs
  // no target-user confirmation since there's no new identity to prove
  // ownership of, and phone-only accounts have no email to send a link to
  // anyway. Same "admin as intermediary" model as the phone OTP flow: the
  // generated password is returned ONCE and must be relayed to the user
  // out-of-band (e.g. over a call) — it's never stored or shown again.
  function handleResetPassword() {
    if (!selected) return;
    setConfirmDialog({
      title: 'Reset Password',
      message: `Reset ${selected.username}'s password? A new password will be generated immediately.`,
      confirmLabel: 'Reset Password',
      danger: true,
      onConfirm: doResetPassword,
    });
  }

  async function doResetPassword() {
    setConfirmDialog(null);
    setResettingPassword(true);
    setError(null);
    setGeneratedPassword(null);
    setCopied(false);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/reset-password`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not reset password.');
        return;
      }
      setGeneratedPassword(data.password);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResettingPassword(false);
    }
  }

  async function handleCopyPassword() {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard API can fail (permissions, non-secure context) — the
      // password is still visible in the field for manual copy either way
    }
  }

  // For an account genuinely locked out (no TOTP device left, so it can't
  // even reach an interactive login stage to fix itself — see
  // adminUsers.js's /reset-totp header). Deletes any leftover device and
  // generates a fresh QR code — shown here to the ADMIN, who relays it to
  // the user (e.g. screen-share on a call) and enters the code the user
  // reads back, same "admin as intermediary" model as the phone OTP reset
  // above.
  function handleResetTotp() {
    if (!selected) return;
    setConfirmDialog({
      title: selected.hasAuthenticator ? 'Reset Authenticator App' : 'Set up Authenticator App',
      message: selected.hasAuthenticator
        ? `Reset ${selected.username}'s authenticator app? Any existing device will be removed immediately and a new QR code generated.`
        : `Set up an authenticator app for ${selected.username}? A QR code will be generated for you to share with them (e.g. on a call) and you'll enter the code they read back.`,
      confirmLabel: selected.hasAuthenticator ? 'Reset Authenticator' : 'Set up Authenticator',
      danger: true,
      onConfirm: doResetTotp,
    });
  }

  async function doResetTotp() {
    setConfirmDialog(null);
    setResettingTotp(true);
    setError(null);
    setTotpChallenge(null);
    setTotpDone(false);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/reset-totp`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not start authenticator reset.');
        return;
      }
      setTotpChallenge(data);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResettingTotp(false);
    }
  }

  async function handleConfirmTotp({ code }) {
    if (!selected) return;
    setConfirmingTotp(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/reset-totp/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      if (data.done) {
        setTotpChallenge(null);
        setTotpDone(true);
        refreshSelectedQuietly();
        return;
      }
      // A wrong code re-shows the same TOTP stage (not a redirect) — same
      // {errors} shape registration.js's /step and claimAccount.js's own
      // TOTP confirm return on a wrong code (see ClaimAccount.jsx's
      // handleConfirmTotp for the same fix).
      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Incorrect code. Please try again.');
        return;
      }
      setError('Unexpected response from the identity provider. Please try again.');
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setConfirmingTotp(false);
    }
  }

  // Passkey equivalent of the authenticator reset above, but genuinely
  // different in shape: a passkey ceremony can only run on the USER's own
  // device (biometric/PIN), so there's nothing for the admin to scan or
  // read back. The backend removes the old passkeys and returns a one-time
  // setup LINK — the admin relays that link (call, SMS, message) and the
  // user opens it on the device they want to use. See adminUsers.js's
  // /reset-webauthn header.
  function handleResetPasskey() {
    if (!selected) return;
    setConfirmDialog({
      title: selected.hasPasskey ? 'Reset Passkeys' : 'Set up Passkey',
      message: `${selected.hasPasskey ? `Reset ${selected.username}'s passkeys? All of their existing passkeys are removed immediately.` : `Set up a passkey for ${selected.username}?`} ${selected.email ? `A one-time setup link will be emailed to ${selected.email} (and shown here as a backup).` : 'They have no email on file, so a one-time setup link will be shown here for you to send them another way.'} ${selected.hasPasskey ? "They can't sign in with a passkey until they open it." : 'It only takes effect once they open it.'}`,
      confirmLabel: selected.hasPasskey ? 'Reset Passkeys' : 'Set up Passkey',
      danger: true,
      onConfirm: doResetPasskey,
    });
  }

  async function doResetPasskey() {
    setConfirmDialog(null);
    setResettingPasskey(true);
    setError(null);
    setPasskeyReset(null);
    setPasskeyLinkCopied(false);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/reset-webauthn`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not start passkey reset.');
        return;
      }
      setPasskeyReset(data);
      refreshSelectedQuietly();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResettingPasskey(false);
    }
  }

  async function handleCopyPasskeyLink() {
    try {
      await navigator.clipboard.writeText(passkeyReset.ceremonyUrl);
      setPasskeyLinkCopied(true);
      setTimeout(() => setPasskeyLinkCopied(false), 2000);
    } catch (e) {
      // clipboard can fail (permissions / non-secure context) — the link is
      // still visible in the field for manual copy either way
    }
  }

  // Unlike the two resets above, SMS/email sign-in aren't real Authentik
  // devices — this just clears the account's own mfa_method attribute (see
  // adminUsers.js's /reset-sms-mfa and /reset-code-mfa). No ceremony/QR
  // needed here: the person is blocked as "incomplete" (same as a
  // never-finished registration) until they use "Continue Registration",
  // which offers every sign-in method again.
  const CODE_MFA_RESET = {
    sms: { label: 'SMS', endpoint: 'reset-sms-mfa', example: 'their phone number changed' },
    code: { label: 'One-time Code', endpoint: 'reset-code-mfa', example: 'their phone number or email changed' },
  };
  // UPDATED (2026-09-23): a one-time code no longer has to be an account's ONLY
  // method — the login screen now offers a genuine choice among whichever
  // methods an account actually has (see Login.jsx's new chooser), so a code
  // alongside an existing authenticator/passkey is real and usable, not
  // silently ignored. "Set up" is now offered for ANY account that doesn't
  // already have a code method, device-holding or not — it never touches
  // existing devices (adminUsers.js's /set-code-mfa only ever PATCHes
  // attributes.mfa_method). The old destructive "Switch" action (which
  // deleted devices to force the code method to matter) is gone from this
  // page for that reason — the same end state, if ever genuinely wanted, is
  // still reachable via the existing separate "Reset Authenticator"/"Reset
  // Passkeys" buttons above, followed by "Set up One-time Code" here.
  const canSetUpCodeMfa = !!selected && !selected.mfaMethod && !!selected.codeChannel;

  function handleResetCodeMfa(kind) {
    if (!selected) return;
    const cfg = CODE_MFA_RESET[kind];
    setConfirmDialog({
      title: `Reset ${cfg.label} Sign-in`,
      message: `Reset ${selected.username}'s ${cfg.label} sign-in? They won't be able to sign in normally afterward — they'll need to use "Continue Registration" to choose a sign-in method again. Only do this if you actually want them to choose again, e.g. ${cfg.example}.`,
      confirmLabel: `Reset ${cfg.label} Sign-in`,
      danger: true,
      onConfirm: () => doResetCodeMfa(kind),
    });
  }

  async function doResetCodeMfa(kind) {
    const cfg = CODE_MFA_RESET[kind];
    setConfirmDialog(null);
    setResettingCodeMfa(kind);
    setError(null);
    setCodeMfaResetDone(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/${cfg.endpoint}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || `Could not reset ${cfg.label} sign-in.`);
        return;
      }
      setCodeMfaResetDone(kind);
      refreshSelectedQuietly();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResettingCodeMfa(null);
    }
  }

  // Admin sets up the one-time code method for an account that has no sign-in
  // method at all (see adminUsers.js's /set-code-mfa). Codes go to the
  // account's OWN verified phone/email, never to the admin.
  function handleSetUpCodeMfa() {
    if (!selected) return;
    const via = selected.codeChannel === 'sms' ? 'their phone' : 'their email';
    setConfirmDialog({
      title: 'Set up One-time Code Sign-in',
      message: `Set up one-time code sign-in for ${selected.username}? A code will be sent to ${via} each time they sign in.`,
      confirmLabel: 'Set up',
      onConfirm: doSetUpCodeMfa,
    });
  }

  async function doSetUpCodeMfa() {
    setConfirmDialog(null);
    setResettingCodeMfa('setup');
    setError(null);
    setCodeMfaResetDone(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/set-code-mfa`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not set up one-time code sign-in.');
        return;
      }
      setCodeMfaResetDone('setup');
      refreshSelectedQuietly();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResettingCodeMfa(null);
    }
  }

  async function handleCancelPhone() {
    if (!selected) return;
    setCancellingPhone(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${selected.pk}/cancel-phone`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setAwaitingCode(false);
      setPhoneMessage(null);
      setPhoneInput('');
      setCodeInput('');
      setResendCooldown(0);
      setShowPhoneForm(false);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setCancellingPhone(false);
    }
  }

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      {/* CONFIRMED BUG (2026-09-09): this page never rendered AdminSidebar
          at all, unlike every other admin page (AdminHierarchy.jsx,
          AdminAuditLog.jsx, etc.) — so navigating from User Directory into
          a specific person's edit page dropped the whole admin nav, with
          no way back except the page's own "Back to Directory" button.
          Same flex layout (`display: flex, gap: 24px`) AdminAuditLog.jsx
          already uses, so the sidebar sits consistently in the same place
          across every admin page. */}
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="users" />
      <div style={cardScroll}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
            {loading ? 'Loading…' : selected ? `Editing: ${selected.username}` : 'User not found'}
            {selected && statusBadge(selected.registrationStatus) && (
              <span style={{
                marginLeft: '8px', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                color: statusBadge(selected.registrationStatus).color,
                background: statusBadge(selected.registrationStatus).background,
              }}>
                {statusBadge(selected.registrationStatus).label}
              </span>
            )}
            {selected?.needsApproval && (
              <span style={{
                marginLeft: '8px', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                color: '#B3261E', background: '#FDECEC',
              }}>
                Approval
              </span>
            )}
          </h1>
          <button
            onClick={() => navigate('/admin/users')}
            style={{ background: '#5F5E5A', border: '1px solid #5F5E5A', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
          >
            ← Back to Directory
          </button>
        </div>

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {selected && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginBottom: '20px' }}>
              {/* Forcibly activating an incomplete registration doesn't
                  fix anything — there's no TOTP device yet either way, so
                  they'd just hit the same "no MFA configured" dead end at
                  login. Only offer this toggle once there's an actual
                  on/off state worth flipping (i.e. TOTP already exists). */}
              {selected.registrationStatus !== 'incomplete' && (
                <button
                  type="button"
                  onClick={handleToggleActive}
                  disabled={togglingActive}
                  style={{
                    background: selected.is_active === false ? '#0F6E56' : '#B3261E',
                    border: 'none',
                    color: '#fff',
                    borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '5px 12px',
                    cursor: togglingActive ? 'not-allowed' : 'pointer',
                    opacity: togglingActive ? 0.6 : 1,
                  }}
                >
                  {togglingActive ? 'Updating…' : selected.is_active === false ? 'Activate' : 'Deactivate'}
                </button>
              )}
              {/* Authentik's own built-in bootstrap superuser — backend
                  already refuses to delete it (adminUsers.js), hide the
                  button too rather than let it look clickable. */}
              {selected.username !== 'akadmin' && (
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={deletingUser}
                  style={{
                    background: '#B3261E',
                    border: 'none',
                    color: '#fff',
                    borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '5px 12px',
                    cursor: deletingUser ? 'not-allowed' : 'pointer',
                    opacity: deletingUser ? 0.6 : 1,
                  }}
                >
                  {deletingUser ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>Working Details & Role</span>
                {!editingDetails && (
                  <button
                    type="button"
                    onClick={handleEditDetails}
                    style={{ background: '#009FE3', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 10px', cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                )}
              </div>

              {!editingDetails ? (
                // Read-only reference — CompleteProfile.jsx / bulk onboarding
                // set these originally; Edit above lets an admin correct them.
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px',
                  padding: '16px', background: 'rgba(255,255,255,0.5)', borderRadius: '10px',
                }}>
                  {ATTRIBUTE_FIELDS.map((f) => (
                    <div key={f.key}>
                      <div style={{ fontSize: '11px', color: '#5F5E5A', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {f.label}
                      </div>
                      <div style={{ fontSize: '14px', color: '#1a1a1a' }}>{selected[f.key] || '—'}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <form onSubmit={handleSaveDetails} style={{ padding: '16px', background: 'rgba(255,255,255,0.5)', borderRadius: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label style={lbl}>Designation</label>
                      <select name="designation" value={detailsForm.designation} onChange={handleDetailsChange} style={{ ...inp, cursor: 'pointer' }}>
                        <option value="">-- Select --</option>
                        {DESIGNATIONS.map((d) => <option key={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Role</label>
                      <input name="role" value={detailsForm.role} onChange={handleDetailsChange} style={inp} placeholder="e.g. Section_Officer, Assistant Executive Engineer, Executive Engineer" />
                    </div>
                    <div>
                      <label style={lbl}>Account Type</label>
                      <select name="accountType" value={detailsForm.accountType} onChange={handleDetailsChange} style={{ ...inp, cursor: 'pointer' }}>
                        <option value="">-- Select --</option>
                        {ACCOUNT_TYPES.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #D3D1C7', paddingTop: '16px', marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#0072B1', margin: '0 0 12px' }}>Posting</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
                      <div>
                        <label style={lbl}>Zone/Office</label>
                        <select name="zone" value={detailsForm.zone} onChange={handleDetailsChange} style={{ ...inp, cursor: 'pointer' }}>
                          <option value="">-- Select --</option>
                          {getZones().map((z) => <option key={z}>{z}</option>)}
                        </select>
                        {isZoneOffice && (
                          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#0F6E56' }}>✅ Office location — no further selection needed.</p>
                        )}
                      </div>

                      {/* All levels always shown — disabled until their own
                          parent is picked, same as CompleteProfile.jsx, so
                          it's clear from the start how many hierarchy
                          levels exist instead of looking like Zone is the
                          only choice. An "office" entry disables the next
                          level outright, since it has no children to pick
                          from anyway. */}
                      <div>
                        <label style={lbl}>Circle</label>
                        <select
                          name="circle" value={detailsForm.circle} onChange={handleDetailsChange}
                          disabled={!detailsForm.zone || isZoneOffice}
                          style={{ ...inp, cursor: (detailsForm.zone && !isZoneOffice) ? 'pointer' : 'not-allowed', opacity: (detailsForm.zone && !isZoneOffice) ? 1 : 0.6 }}
                        >
                          <option value="">— None (stop at Zone) —</option>
                          {getCircles().map((c) => <option key={c}>{c}</option>)}
                        </select>
                        {isCircleOffice && (
                          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#0F6E56' }}>✅ Office location — no further selection needed.</p>
                        )}
                      </div>

                      <div>
                        <label style={lbl}>Division</label>
                        <select
                          name="division" value={detailsForm.division} onChange={handleDetailsChange}
                          disabled={!detailsForm.circle || isCircleOffice}
                          style={{ ...inp, cursor: (detailsForm.circle && !isCircleOffice) ? 'pointer' : 'not-allowed', opacity: (detailsForm.circle && !isCircleOffice) ? 1 : 0.6 }}
                        >
                          <option value="">— None (stop at Circle) —</option>
                          {getDivisions().map((d) => <option key={d}>{d}</option>)}
                        </select>
                        {isDivisionOffice && (
                          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#0F6E56' }}>✅ Office location — no further selection needed.</p>
                        )}
                      </div>

                      <div>
                        <label style={lbl}>Sub-Division</label>
                        <select
                          name="subdivision" value={detailsForm.subdivision} onChange={handleDetailsChange}
                          disabled={!detailsForm.division || isDivisionOffice}
                          style={{ ...inp, cursor: (detailsForm.division && !isDivisionOffice) ? 'pointer' : 'not-allowed', opacity: (detailsForm.division && !isDivisionOffice) ? 1 : 0.6 }}
                        >
                          <option value="">— None (stop at Division) —</option>
                          {getSubDivisions().map((s) => <option key={s}>{s}</option>)}
                        </select>
                        {isSubdivisionOffice && (
                          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#0F6E56' }}>✅ Office location — no further selection needed.</p>
                        )}
                      </div>

                      <div>
                        <label style={lbl}>Section</label>
                        <select
                          name="section" value={detailsForm.section} onChange={handleDetailsChange}
                          disabled={!detailsForm.subdivision || isSubdivisionOffice}
                          style={{ ...inp, cursor: (detailsForm.subdivision && !isSubdivisionOffice) ? 'pointer' : 'not-allowed', opacity: (detailsForm.subdivision && !isSubdivisionOffice) ? 1 : 0.6 }}
                        >
                          <option value="">— None (stop at Sub-Division) —</option>
                          {getSections().map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" disabled={savingDetails} style={{ ...btn, marginTop: 0, padding: '7px 18px', fontSize: '13px', opacity: savingDetails ? 0.6 : 1, cursor: savingDetails ? 'not-allowed' : 'pointer' }}>
                      {savingDetails ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelDetails}
                      disabled={savingDetails}
                      style={{
                        background: '#B3261E', border: 'none', color: '#fff',
                        borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                        padding: '7px 18px', cursor: savingDetails ? 'not-allowed' : 'pointer',
                        opacity: savingDetails ? 0.6 : 1,
                      }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={lbl}>Groups</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {(selected.groups || [])
                  .filter((g) => !EXCLUDED_GROUPS.has(g.name))
                  .map((g) => (
                    <span
                      key={g.pk}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontSize: '12px', fontWeight: '600', padding: '4px 6px 4px 10px',
                        borderRadius: '14px', color: '#0072B1', background: '#E6F5FC',
                      }}
                    >
                      {g.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveGroup(g)}
                        disabled={removingGroupPk === g.pk}
                        title={`Remove from ${g.name}`}
                        style={{
                          background: 'none', border: 'none', color: '#0072B1', cursor: removingGroupPk === g.pk ? 'not-allowed' : 'pointer',
                          fontSize: '13px', lineHeight: 1, padding: '2px 4px', opacity: removingGroupPk === g.pk ? 0.5 : 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                {(selected.groups || []).filter((g) => !EXCLUDED_GROUPS.has(g.name)).length === 0 && (
                  <span style={{ fontSize: '13px', color: '#666' }}>No groups.</span>
                )}
              </div>
              <form onSubmit={handleAddGroup} style={{ display: 'flex', gap: '10px' }}>
                <select
                  value={groupToAdd}
                  onChange={(e) => setGroupToAdd(e.target.value)}
                  style={{ ...inp, width: 'auto', maxWidth: '260px' }}
                >
                  <option value="">Add to group…</option>
                  {allGroups
                    .filter((g) => !(selected.groups || []).some((sg) => String(sg.pk) === String(g.pk)))
                    .map((g) => (
                      <option key={g.pk} value={g.pk}>{g.name}</option>
                    ))}
                </select>
                <button
                  type="submit"
                  disabled={!groupToAdd || addingGroup}
                  style={(!groupToAdd || addingGroup) ? btnDisabled : { ...btn, width: 'auto', marginTop: 0 }}
                >
                  {addingGroup ? 'Adding…' : 'Add'}
                </button>
              </form>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '10px' }}>
                Account Recovery
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingPassword ? 0.6 : 1, cursor: resettingPassword ? 'not-allowed' : 'pointer' }}
                >
                  {resettingPassword ? 'Resetting…' : '🔑 Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={handleResetTotp}
                  disabled={resettingTotp}
                  style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingTotp ? 0.6 : 1, cursor: resettingTotp ? 'not-allowed' : 'pointer' }}
                >
                  {resettingTotp ? 'Starting…' : selected.hasAuthenticator ? '📱 Reset Authenticator App' : '📱 Set up Authenticator App'}
                </button>
                <button
                  type="button"
                  onClick={handleResetPasskey}
                  disabled={resettingPasskey}
                  style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingPasskey ? 0.6 : 1, cursor: resettingPasskey ? 'not-allowed' : 'pointer' }}
                >
                  {resettingPasskey ? 'Starting…' : selected.hasPasskey ? '🔐 Reset Passkeys' : '🔐 Set up Passkey'}
                </button>
                {selected.mfaMethod === 'sms' && (
                  <button
                    type="button"
                    onClick={() => handleResetCodeMfa('sms')}
                    disabled={!!resettingCodeMfa}
                    style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingCodeMfa ? 0.6 : 1, cursor: resettingCodeMfa ? 'not-allowed' : 'pointer' }}
                  >
                    {resettingCodeMfa === 'sms' ? 'Resetting…' : '📵 Reset SMS Sign-in'}
                  </button>
                )}
                {selected.mfaMethod === 'code' && (
                  <button
                    type="button"
                    onClick={() => handleResetCodeMfa('code')}
                    disabled={!!resettingCodeMfa}
                    style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingCodeMfa ? 0.6 : 1, cursor: resettingCodeMfa ? 'not-allowed' : 'pointer' }}
                  >
                    {resettingCodeMfa === 'code' ? 'Resetting…' : '🔢 Reset One-time Code Sign-in'}
                  </button>
                )}
                {canSetUpCodeMfa && (
                  <button
                    type="button"
                    onClick={handleSetUpCodeMfa}
                    disabled={!!resettingCodeMfa}
                    style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 18px', opacity: resettingCodeMfa ? 0.6 : 1, cursor: resettingCodeMfa ? 'not-allowed' : 'pointer' }}
                  >
                    {resettingCodeMfa === 'setup' ? 'Setting up…' : '🔢 Set up One-time Code Sign-in'}
                  </button>
                )}
              </div>
              {codeMfaResetDone === 'setup' && (
                <p style={{ fontSize: '13px', color: '#0F6E56', marginTop: '10px' }}>
                  One-time code sign-in is set up — {selected.username} will get a code by{' '}
                  {selected.codeChannel === 'sms' ? 'SMS' : 'email'} each time they sign in.
                </p>
              )}
              {codeMfaResetDone && codeMfaResetDone !== 'setup' && (
                <p style={{ fontSize: '13px', color: '#0F6E56', marginTop: '10px' }}>
                  {CODE_MFA_RESET[codeMfaResetDone].label} sign-in reset — {selected.username} will need to use "Continue
                  Registration" to choose a sign-in method again next time they sign in.
                </p>
              )}
              <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0' }}>
                Buttons show what this account actually has: "Reset" removes the existing factor
                and starts a new one, "Set up" adds one it doesn't have yet. Authenticator: have the
                user on a call, ready to scan. Passkey: gives you a one-time setup link (also emailed) —
                they open it on the device they want to use. "Set up One-time Code" never removes an
                existing authenticator or passkey — the person will see a choice of all their sign-in
                methods at their next login, not just the code.
              </p>

              {passkeyReset && (
                <div style={{ marginTop: '14px' }}>
                  {passkeyReset.emailedTo && (
                    <p style={{ fontSize: '13px', color: '#0F6E56', margin: '0 0 8px' }}>
                      ✓ Setup link emailed to {passkeyReset.emailedTo}.
                    </p>
                  )}
                  {passkeyReset.emailFailed && (
                    <p style={{ fontSize: '13px', color: '#B3261E', margin: '0 0 8px' }}>
                      Couldn't email the link — send it to them yourself using the link below.
                    </p>
                  )}
                  {!passkeyReset.emailedTo && !passkeyReset.emailFailed && (
                    <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px' }}>
                      No email on file — send the link below to them yourself.
                    </p>
                  )}
                  <label style={lbl}>
                    Passkey setup link for {selected.username} — it expires in{' '}
                    {passkeyReset.expiresInMinutes} minutes and works once
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      style={{ ...inp, fontFamily: 'monospace', fontSize: '12px' }}
                      type="text"
                      value={passkeyReset.ceremonyUrl}
                      readOnly
                      onFocus={(e) => e.target.select()}
                    />
                    <button type="button" onClick={handleCopyPasskeyLink} style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 16px' }}>
                      {passkeyLinkCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '6px 0 0' }}>
                    {passkeyReset.removed} existing passkey(s) removed. The link must be opened on the
                    user's own device — a passkey can't be created from here.
                  </p>
                </div>
              )}

              {generatedPassword && (
                <div style={{ marginTop: '14px' }}>
                  <label style={lbl}>New password — share this with the user, it won't be shown again</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      style={{ ...inp, fontFamily: 'monospace' }}
                      type="text"
                      value={generatedPassword}
                      readOnly
                      onFocus={(e) => e.target.select()}
                    />
                    <button type="button" onClick={handleCopyPassword} style={{ ...btn, width: 'auto', marginTop: 0, padding: '10px 16px' }}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {totpChallenge?.raw?.config_url && (
                <div style={{ marginTop: '14px' }}>
                  <TotpSetupStep
                    configUrl={totpChallenge.raw.config_url}
                    loading={confirmingTotp}
                    onSubmit={handleConfirmTotp}
                    description={`Have ${selected.username} scan this with their authenticator app, then enter the code they read back to you.`}
                  />
                </div>
              )}

              {totpDone && (
                <p style={{ fontSize: '13px', color: '#0F6E56', marginTop: '10px' }}>
                  Authenticator reset — {selected.username} can now sign in with the new device.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '16px' }}>
                <span>Current email: <strong>{selected.email || 'none'}</strong></span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setShowEmailForm((v) => !v)}
                    disabled={selected.is_active === false}
                    style={{ background: '#009FE3', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 10px', cursor: selected.is_active === false ? 'not-allowed' : 'pointer', opacity: selected.is_active === false ? 0.5 : 1 }}
                  >
                    {showEmailForm ? 'Close' : 'Update'}
                  </button>
                  {selected.email && (
                    <button
                      type="button"
                      onClick={handleRemoveEmail}
                      disabled={removingEmail || !selected.phone}
                      title={!selected.phone ? "Can't remove — user has no phone number to fall back on" : undefined}
                      style={{ background: '#B3261E', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 10px', cursor: (removingEmail || !selected.phone) ? 'not-allowed' : 'pointer', opacity: (removingEmail || !selected.phone) ? 0.5 : 1 }}
                    >
                      {removingEmail ? 'Removing…' : 'Remove'}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '16px' }}>
                <span>Current phone: <strong>{selected.phone || 'none'}</strong></span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setShowPhoneForm((v) => !v)}
                    disabled={selected.is_active === false}
                    style={{ background: '#009FE3', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 10px', cursor: selected.is_active === false ? 'not-allowed' : 'pointer', opacity: selected.is_active === false ? 0.5 : 1 }}
                  >
                    {showPhoneForm ? 'Close' : 'Update'}
                  </button>
                  {selected.phone && (
                    <button
                      type="button"
                      onClick={handleRemovePhone}
                      disabled={removingPhone || !selected.email}
                      title={!selected.email ? "Can't remove — user has no email to fall back on" : undefined}
                      style={{ background: '#B3261E', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 10px', cursor: (removingPhone || !selected.email) ? 'not-allowed' : 'pointer', opacity: (removingPhone || !selected.email) ? 0.5 : 1 }}
                    >
                      {removingPhone ? 'Removing…' : 'Remove'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {selected.is_active === false && (
              <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px' }}>
                {selected.registrationStatus === 'incomplete'
                  ? "This account hasn't finished registration yet. Request the user to complete their registration — email or phone changes aren't available until then."
                  : 'This account is deactivated. Reactivate it above before changing email or phone number.'}
              </div>
            )}

            {/* Email — propose only, user confirms independently. Collapsed
                until "Update" above is clicked. */}
            {showEmailForm && (
              <form onSubmit={handleSendEmail} style={{ marginBottom: '28px' }}>
                {/* ADDED (2026-11-04): `error` is one shared page-wide state,
                    rendered at the very top of the page (line ~1054) — on a
                    long profile, an error from THIS form (e.g. "already
                    registered to another account") landed off-screen above
                    the fold, so a duplicate-email attempt looked like it
                    silently did nothing instead of being refused. Same
                    state, just also rendered right where this form actually
                    is. */}
                {error && (
                  <div style={{ background: '#FDECEC', color: '#B3261E', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '10px' }}>
                    {error}
                  </div>
                )}
                <label style={lbl}>New email address</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    style={{ ...inp, maxWidth: '280px' }}
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="new.email@example.com"
                    disabled={selected.is_active === false}
                  />
                  <button type="submit" disabled={emailSending || !emailInput.trim() || selected.is_active === false} style={(emailSending || !emailInput.trim() || selected.is_active === false) ? btnDisabled : btn}>
                    {emailSending ? 'Sending…' : 'Send Link'}
                  </button>
                </div>
                {emailMessage && (
                  <p style={{ fontSize: '13px', color: '#0F6E56', marginTop: '8px' }}>{emailMessage}</p>
                )}
                {emailProposed && (
                  <button
                    type="button"
                    onClick={handleCancelEmail}
                    disabled={cancellingEmail}
                    style={{ ...btnSecondary, borderColor: '#D3D1C7', color: '#666', marginTop: '8px', opacity: cancellingEmail ? 0.6 : 1 }}
                  >
                    {cancellingEmail ? 'Cancelling…' : 'Cancel this request'}
                  </button>
                )}
              </form>
            )}

            {/* Phone — two-step: send code, then admin enters it. Collapsed
                until "Update" above is clicked. */}
            {showPhoneForm && (
            <form onSubmit={awaitingCode ? handleConfirmPhone : handleSendPhoneCode}>
              {/* Same fix as the email form above — see its own comment. This
                  is specifically what was reported live: proposing a number
                  already on another account returned a real error, but it
                  rendered off-screen at the top of the page, so the "Send
                  Code" button just looked like it quietly did nothing and
                  went back to normal after the cooldown. */}
              {error && (
                <div style={{ background: '#FDECEC', color: '#B3261E', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '10px' }}>
                  {error}
                </div>
              )}
              <label style={lbl}>New phone number (10 digits)</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: awaitingCode ? '12px' : 0 }}>
                <input
                  style={{ ...inp, maxWidth: '160px' }}
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="9876543210"
                  disabled={awaitingCode || selected.is_active === false}
                />
                {!awaitingCode && (
                  <button type="submit" disabled={phoneSending || phoneInput.length !== 10 || selected.is_active === false} style={(phoneSending || phoneInput.length !== 10 || selected.is_active === false) ? btnDisabled : btn}>
                    {phoneSending ? 'Sending…' : 'Send Code'}
                  </button>
                )}
              </div>

              {phoneMessage && (
                <p style={{ fontSize: '13px', color: '#0F6E56', marginBottom: '12px' }}>{phoneMessage}</p>
              )}

              {awaitingCode && (
                <>
                  <label style={lbl}>Code (ask the user for it)</label>
                  {/* CHANGED (2026-11-04): this whole step used to reuse the
                      full-size inp/btn styles built for standalone pages
                      (e.g. Login.jsx) — oversized next to the compact
                      Update/Remove buttons just above on this same profile
                      page. Smaller, scoped to just this code-entry step. */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      style={{ ...smallInp, textAlign: 'center', letterSpacing: '3px', maxWidth: '120px' }}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                    />
                    <button type="submit" disabled={confirming || codeInput.length !== 6} style={(confirming || codeInput.length !== 6) ? smallBtnDisabled : smallBtn}>
                      {confirming ? 'Confirming…' : 'Confirm'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={handleResendPhoneCode}
                      disabled={resendingPhone || resendCooldown > 0}
                      style={{ ...smallBtn, opacity: (resendingPhone || resendCooldown > 0) ? 0.6 : 1, cursor: (resendingPhone || resendCooldown > 0) ? 'not-allowed' : 'pointer' }}
                    >
                      {resendingPhone ? 'Resending…' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelPhone}
                      disabled={cancellingPhone}
                      style={{ ...smallBtnSecondary, opacity: cancellingPhone ? 0.6 : 1 }}
                    >
                      {cancellingPhone ? 'Cancelling…' : 'Cancel this request'}
                    </button>
                  </div>
                </>
              )}
            </form>
            )}

            {/* Super-admin only, same gate as the standalone Audit Log page —
                see backend/src/routes/auditLog.js. Reuses that same page's
                component in "embedded" mode, pre-filtered to this one user,
                and curated ("clean") to just login/logout/app access/
                password reset/authenticator setup — full detail (including
                registration-stage-wise entries and raw flow-executor churn)
                is one click away on the standalone Audit Log page instead. */}
            {isSuperAdmin && (
              <div style={{ marginTop: '28px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
                    Activity
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    {selected.loginAt && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#F7F6F2', border: '1px solid #eee', borderRadius: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#5F5E5A', fontWeight: '500' }}>Session Timer</span>
                        <SessionTimer loginAt={selected.loginAt} style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' }} />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/audit-log?username=${encodeURIComponent(selected.username)}`)}
                      style={{ background: 'none', border: 'none', color: '#009FE3', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                    >
                      View full history →
                    </button>
                  </div>
                </div>
                <AdminAuditLog embedded clean usernameFilter={selected.username} userPk={selected.pk} />
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <ConfirmDialog dialog={confirmDialog} onCancel={() => setConfirmDialog(null)} />
      <DeleteUserDialog dialog={deleteDialog} onCancel={() => setDeleteDialog(null)} />
      </div>
    </>
  );
}
